import moment from "moment";
import { API_URL } from "src/Common/function";
import { getActiveCompany } from "src/Common/tenant";

// Every per-tenant file hangs off one /files prefix: /files/<tenant>/<subdir>/...
// The tenant used to be the first path segment, which made each one look like
// its own top-level route and forced a reverse-proxy rule per tenant. As a
// segment inside a fixed route, one rule covers every tenant forever.
//
// The company here is the ACTIVE one: the entered tenant when a Super Admin is
// acting inside one, otherwise the user's own.
const tenantFiles = () => `${API_URL}/files/${getActiveCompany()}`;

// Same prefix for a tenant named explicitly rather than taken from the session.
// The technician's form viewer is a public route with no logged-in user, so
// getActiveCompany() is undefined there and any URL built from it 404s; the form
// payload carries its own `company`, which is the only tenant available.
const tenantFilesFor = (company) => `${API_URL}/files/${company}`;

export const getLogoUrlFor = (company) =>
  `${tenantFilesFor(company)}/company/logo.png`;

export const getBranchLogoUrlFor = (company, branchId) =>
  `${tenantFilesFor(company)}/branch/branch_${branchId}_logo.png`;

export const getAvatarUrl = (imageName) => {
  return `${tenantFiles()}/avatars/${imageName}`;
};

export const getUploadUrl = (fileName) => {
  return `${tenantFiles()}/uploads/${encodeURIComponent(fileName)}`;
};

export const getPOReceiptUrl = (fileName) => {
  return `${tenantFiles()}/poreceipt/${encodeURIComponent(fileName)}`;
};

export const getLogoUrl = () => {
  return `${tenantFiles()}/company/logo.png?${Date.now()}`;
};

export const getBranchLogoUrl = (branchId) => {
  return `${tenantFiles()}/branch/branch_${branchId}_logo.png?${Date.now()}`;
};

export const getReportUrl = (fileName) => {
  return `${tenantFiles()}/reports/${encodeURIComponent(fileName)}`;
};

export const getRealFileName = (fileName) => {
  if (fileName) {
    const lastDotIndex = fileName.lastIndexOf(".");
    const fileNameWithoutExtension = fileName.substring(0, lastDotIndex);
    const fileNameExtension = fileName.substring(lastDotIndex + 1);

    const lastHyphenIndex = fileNameWithoutExtension.lastIndexOf("-");
    const newFileName = fileNameWithoutExtension.substring(0, lastHyphenIndex);

    return newFileName + "." + fileNameExtension;
  }
};

// export const getPOItemAttachmentRealFileName = (fileName) => {
//   if (fileName) {
//     let fileNameParts = fileName.split(".");
//     return fileName.split("---")[0] + "." + fileNameParts.pop();
//   }
// };

export const getImageUrl = (imagePath) => {
  return process.env.PUBLIC_URL + imagePath;
};

export const formatDate = (dateString, formatString = "MM/DD/YYYY - h:mm A") => {
  const timestamp = Date.parse(dateString);
  return moment(timestamp).format(formatString);
};

export const formatTime = (dateString) => {
  const timestamp = Date.parse(dateString);
  return moment(timestamp).format("h:mm A");
};

export const UtcToMst = (date) => {
  if (date) {
    return moment.utc(date).tz("America/Phoenix").format("YYYY-MM-DD HH:mm");
  } else {
    return null;
  }
};

export const toLocalDate = (date) => {
  const formattedString = moment.utc(date).format("YYYY-MM-DD");
  return moment(formattedString, "YYYY-MM-DD").toDate();
};

// Parse a backend `date` value (time-off start/end) into a LOCAL Date.
// `new Date("2026-06-06")` parses as UTC midnight, which renders as the previous
// calendar day in negative-UTC-offset timezones — the off-by-one that made the
// web app show dates a day earlier than the technician app. moment() interprets
// the value in local time, which correctly recovers the intended day for both
// shapes the backend sends: a plain "YYYY-MM-DD" string, and the full ISO
// timestamp ("...T15:00:00Z") that a pg `date` column serializes to once it has
// been read into a Date on a non-UTC server.
export const parseLocalDate = (value) => {
  if (!value) return null;
  return moment(value).toDate();
};

export const getServiceTicketUrl = (number) => {
  return `${tenantFiles()}/serviceticket/service_ticket_${number}.pdf`;
};

export const convertToArizonaTime = (utcDate) => {
  return moment.utc(utcDate).tz("America/Phoenix").format("YYYY-MM-DD HH:mm:ss");
};

export const getOrdinalSuffix = (day) => {
  const suffixes = ["th", "st", "nd", "rd"];
  const remainder = day % 100;

  return day + (suffixes[(remainder - 20) % 10] || suffixes[remainder] || suffixes[0]);
};

// ✅ Correct: Week assigned to month with majority (4+) of its days
export const getWeekNumberInMonth = (weekStartDate, targetMonth, targetYear) => {
  const startOfMonth = new Date(targetYear, targetMonth, 1);
  const endOfMonth = new Date(targetYear, targetMonth + 1, 0);

  // Start scanning from 6 weeks before just in case
  const scanStart = getStartOfWeek(new Date(startOfMonth.getFullYear(), startOfMonth.getMonth(), 1 - 42));
  let currentWeekStart = new Date(scanStart);
  let weekIndex = 0;

  while (currentWeekStart <= endOfMonth) {
    let daysInTargetMonth = 0;

    for (let i = 0; i < 7; i++) {
      const d = new Date(currentWeekStart);
      d.setDate(d.getDate() + i);
      if (d.getMonth() === targetMonth && d.getFullYear() === targetYear) {
        daysInTargetMonth++;
      }
    }

    const isThisWeek = currentWeekStart.getTime() === weekStartDate.getTime();

    if (daysInTargetMonth >= 4) {
      weekIndex++;
      if (isThisWeek) return weekIndex;
    } else if (isThisWeek) {
      return null;
    }

    currentWeekStart.setDate(currentWeekStart.getDate() + 7);
  }

  return null;
};

export const getMajorityMonthOfWeek = (weekStartDate) => {
  const monthCount = {};

  for (let i = 0; i < 7; i++) {
    const d = new Date(weekStartDate);
    d.setDate(d.getDate() + i);
    const key = `${d.getFullYear()}-${d.getMonth()}`;
    monthCount[key] = (monthCount[key] || 0) + 1;
  }

  const majority = Object.entries(monthCount).reduce((a, b) => (b[1] > a[1] ? b : a));
  const [year, month] = majority[0].split("-").map(Number);

  return { year, month };
};

export const getStartOfWeek = (date) => {
  const weekStart = new Date(date);
  weekStart.setDate(weekStart.getDate() - weekStart.getDay());
  weekStart.setHours(0, 0, 0, 0);
  return weekStart;
};

export const getDaysOfWeekByYearMonthWeek = (year, month, targetWeekNumber) => {
  const endOfMonth = new Date(year, month + 1, 0);

  // Go back ~6 weeks just in case
  const scanStart = getStartOfWeek(new Date(year, month, 1 - 42));
  let currentWeekStart = new Date(scanStart);
  let weekIndex = 0;

  while (currentWeekStart <= endOfMonth) {
    let daysInTargetMonth = 0;

    for (let i = 0; i < 7; i++) {
      const d = new Date(currentWeekStart);
      d.setDate(d.getDate() + i);
      if (d.getMonth() === month && d.getFullYear() === year) {
        daysInTargetMonth++;
      }
    }

    const belongsToMonth = daysInTargetMonth >= 4;

    if (belongsToMonth) {
      weekIndex++;
      if (weekIndex === targetWeekNumber) {
        const days = [];
        for (let i = 0; i < 7; i++) {
          const day = new Date(currentWeekStart);
          day.setDate(currentWeekStart.getDate() + i);
          const dayObj = { day: day.getDate() };
          days.push(dayObj);
        }
        return days;
      }
    }

    currentWeekStart.setDate(currentWeekStart.getDate() + 7);
  }

  return null; // Not found
};

// Money as it appears on a purchase order: comma-grouped, always 2dp, no
// currency symbol — callers add "$" where the layout wants one. Shared so the
// creation form, the detail page and the printed document round identically.
export const formatMoney = (value) =>
  Number(value || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
